"use client"

import { useState, useEffect } from "react"
import "./ColonyReports.css"

const ColonyReports = ({ gameState }) => {
  const [activeReport, setActiveReport] = useState("overview")
  const [reportData, setReportData] = useState({})

  useEffect(() => {
    generateReportData()
  }, [gameState])

  const generateReportData = () => {
    const { resources, colonyHealth, colonyMorale, buildings, user } = gameState

    // Calculate efficiency ratings
    const resourceEfficiency =
      (Object.values(resources).reduce((acc, val) => acc + val, 0) / (Object.keys(resources).length * 500)) * 100
    const buildingEfficiency = buildings.reduce((acc, building) => acc + building.health, 0) / buildings.length
    const populationGrowth = Math.random() * 5 - 2 // Simulated growth rate
    const threatLevel = calculateThreatLevel()

    setReportData({
      overview: {
        totalPopulation: resources.population,
        colonyAge: Math.floor(Math.random() * 365) + 100, // Days since founding
        resourceEfficiency: Math.round(resourceEfficiency),
        buildingEfficiency: Math.round(buildingEfficiency),
        overallRating: calculateOverallRating(),
      },
      population: {
        total: resources.population,
        growth: populationGrowth,
        births: Math.floor(Math.random() * 10) + 2,
        deaths: Math.floor(Math.random() * 5),
        morale: colonyMorale,
        health: colonyHealth,
        demographics: generateDemographics(),
      },
      resources: {
        current: resources,
        consumption: calculateConsumption(),
        production: calculateProduction(),
        efficiency: resourceEfficiency,
        reserves: calculateReserves(),
      },
      infrastructure: {
        buildings: buildings,
        totalBuildings: buildings.length,
        averageHealth: buildingEfficiency,
        constructionQueue: generateConstructionQueue(),
        maintenanceNeeded: buildings.filter((b) => b.health < 70),
      },
      threats: {
        level: threatLevel,
        active: generateActiveThreats(),
        environmental: generateEnvironmentalThreats(),
        recommendations: generateThreatRecommendations(),
      },
      achievements: {
        recent: generateRecentAchievements(),
        milestones: generateMilestones(),
        progress: calculateProgress(),
      },
    })
  }

  const calculateThreatLevel = () => {
    const { resources, colonyHealth } = gameState
    let threatScore = 0

    if (resources.food < 200) threatScore += 30
    if (resources.energy < 200) threatScore += 25
    if (resources.water < 200) threatScore += 35
    if (colonyHealth < 50) threatScore += 20

    if (threatScore > 70) return "Critical"
    if (threatScore > 40) return "High"
    if (threatScore > 20) return "Medium"
    return "Low"
  }

  const calculateOverallRating = () => {
    const { resources, colonyHealth, colonyMorale } = gameState
    const avgResources = Object.values(resources).reduce((acc, val) => acc + val, 0) / Object.keys(resources).length
    const overall = (avgResources / 500) * 40 + (colonyHealth / 100) * 30 + (colonyMorale / 100) * 30

    if (overall > 80) return "Excellent"
    if (overall > 60) return "Good"
    if (overall > 40) return "Fair"
    if (overall > 20) return "Poor"
    return "Critical"
  }

  const generateDemographics = () => ({
    adults: Math.floor(gameState.resources.population * 0.7),
    children: Math.floor(gameState.resources.population * 0.2),
    elderly: Math.floor(gameState.resources.population * 0.1),
    scientists: Math.floor(gameState.resources.population * 0.15),
    engineers: Math.floor(gameState.resources.population * 0.12),
    farmers: Math.floor(gameState.resources.population * 0.08),
    medics: Math.floor(gameState.resources.population * 0.05),
  })

  const calculateConsumption = () => ({
    food: Math.floor(gameState.resources.population * 0.5),
    energy: Math.floor(gameState.resources.population * 0.3),
    water: Math.floor(gameState.resources.population * 0.4),
    oxygen: Math.floor(gameState.resources.population * 0.6),
  })

  const calculateProduction = () => ({
    food: gameState.buildings.filter((b) => b.name === "Hydroponic Farm").length * 50,
    energy: gameState.buildings.filter((b) => b.name === "Solar Array").length * 75,
    materials: gameState.buildings.filter((b) => b.name === "Mining Facility").length * 40,
    knowledge: gameState.buildings.filter((b) => b.name === "Research Lab").length * 25,
  })

  const calculateReserves = () => ({
    food: Math.floor(gameState.resources.food / calculateConsumption().food),
    energy: Math.floor(gameState.resources.energy / calculateConsumption().energy),
    water: Math.floor(gameState.resources.water / calculateConsumption().water),
  })

  const generateConstructionQueue = () => [
    { name: "Medical Bay", progress: 65, eta: "2 days" },
    { name: "Recreation Center", progress: 30, eta: "5 days" },
    { name: "Advanced Lab", progress: 10, eta: "8 days" },
  ]

  const generateActiveThreats = () => [
    { name: "Solar Storm", severity: "Medium", eta: "3 hours" },
    { name: "Equipment Failure", severity: "Low", location: "Sector 7" },
    { name: "Resource Shortage", severity: "High", type: "Food" },
  ]

  const generateEnvironmentalThreats = () => [
    { name: "Dust Storm", probability: 25, impact: "Solar efficiency -30%" },
    { name: "Radiation Spike", probability: 15, impact: "Health -10%" },
    { name: "Temperature Drop", probability: 40, impact: "Energy consumption +20%" },
  ]

  const generateThreatRecommendations = () => [
    "Increase food production by building more hydroponic farms",
    "Deploy radiation shields before the next solar storm",
    "Conduct maintenance on critical systems in Sector 7",
    "Stockpile emergency supplies for dust storm season",
  ]

  const generateRecentAchievements = () => [
    { name: "Population Milestone", description: "Reached 1000 colonists", date: "2 days ago" },
    { name: "Research Breakthrough", description: "Advanced hydroponics unlocked", date: "1 week ago" },
    { name: "Zero Casualties", description: "30 days without incidents", date: "1 month ago" },
  ]

  const generateMilestones = () => [
    { name: "First Settlement", completed: true, date: "Colony Day 1" },
    { name: "Self-Sufficient Food", completed: true, date: "Colony Day 45" },
    { name: "1000 Population", completed: true, date: "Colony Day 120" },
    { name: "Advanced Technology", completed: false, progress: 75 },
    { name: "Terraforming Begin", completed: false, progress: 25 },
  ]

  const calculateProgress = () => ({
    survival: 85,
    expansion: 60,
    technology: 45,
    sustainability: 70,
  })

  const getStatusColor = (value, type = "percentage") => {
    if (type === "percentage") {
      if (value >= 80) return "#4caf50"
      if (value >= 60) return "#ff9800"
      if (value >= 40) return "#ff5722"
      return "#f44336"
    }

    if (type === "threat") {
      switch (value) {
        case "Low":
          return "#4caf50"
        case "Medium":
          return "#ff9800"
        case "High":
          return "#ff5722"
        case "Critical":
          return "#f44336"
        default:
          return "#4caf50"
      }
    }
  }

  const renderOverviewReport = () => (
    <div className="report-content">
      <div className="report-header">
        <h2>Colony Overview Report</h2>
        <div className="report-date">Generated: {new Date().toLocaleString()}</div>
      </div>

      <div className="overview-grid">
        <div className="overview-card">
          <div className="card-icon">👥</div>
          <div className="card-content">
            <h3>Population</h3>
            <div className="card-value">{reportData.overview?.totalPopulation?.toLocaleString()}</div>
            <div className="card-subtitle">Active Colonists</div>
          </div>
        </div>

        <div className="overview-card">
          <div className="card-icon">📅</div>
          <div className="card-content">
            <h3>Colony Age</h3>
            <div className="card-value">{reportData.overview?.colonyAge}</div>
            <div className="card-subtitle">Days Since Founding</div>
          </div>
        </div>

        <div className="overview-card">
          <div className="card-icon">⚡</div>
          <div className="card-content">
            <h3>Resource Efficiency</h3>
            <div className="card-value" style={{ color: getStatusColor(reportData.overview?.resourceEfficiency) }}>
              {reportData.overview?.resourceEfficiency}%
            </div>
            <div className="card-subtitle">Overall Performance</div>
          </div>
        </div>

        <div className="overview-card">
          <div className="card-icon">🏗️</div>
          <div className="card-content">
            <h3>Infrastructure</h3>
            <div className="card-value" style={{ color: getStatusColor(reportData.overview?.buildingEfficiency) }}>
              {reportData.overview?.buildingEfficiency}%
            </div>
            <div className="card-subtitle">Building Health</div>
          </div>
        </div>
      </div>

      <div className="status-summary">
        <h3>
          Colony Status:{" "}
          <span style={{ color: getStatusColor(reportData.overview?.resourceEfficiency) }}>
            {reportData.overview?.overallRating}
          </span>
        </h3>
        <div className="status-details">
          <div className="status-item">
            <span className="status-label">Health:</span>
            <div className="status-bar">
              <div
                className="status-fill"
                style={{
                  width: `${gameState.colonyHealth}%`,
                  backgroundColor: getStatusColor(gameState.colonyHealth),
                }}
              />
            </div>
            <span className="status-value">{gameState.colonyHealth}%</span>
          </div>
          <div className="status-item">
            <span className="status-label">Morale:</span>
            <div className="status-bar">
              <div
                className="status-fill"
                style={{
                  width: `${gameState.colonyMorale}%`,
                  backgroundColor: getStatusColor(gameState.colonyMorale),
                }}
              />
            </div>
            <span className="status-value">{gameState.colonyMorale}%</span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderPopulationReport = () => (
    <div className="report-content">
      <div className="report-header">
        <h2>Population Analysis</h2>
        <div className="growth-indicator">
          <span className={`growth-value ${reportData.population?.growth >= 0 ? "positive" : "negative"}`}>
            {reportData.population?.growth >= 0 ? "+" : ""}
            {reportData.population?.growth?.toFixed(1)}%
          </span>
          <span className="growth-label">Monthly Growth</span>
        </div>
      </div>

      <div className="population-stats">
        <div className="stat-group">
          <h3>Demographics</h3>
          <div className="demo-grid">
            <div className="demo-item">
              <span className="demo-icon">👨‍💼</span>
              <span className="demo-label">Adults</span>
              <span className="demo-value">{reportData.population?.demographics?.adults}</span>
            </div>
            <div className="demo-item">
              <span className="demo-icon">👶</span>
              <span className="demo-label">Children</span>
              <span className="demo-value">{reportData.population?.demographics?.children}</span>
            </div>
            <div className="demo-item">
              <span className="demo-icon">👴</span>
              <span className="demo-label">Elderly</span>
              <span className="demo-value">{reportData.population?.demographics?.elderly}</span>
            </div>
          </div>
        </div>

        <div className="stat-group">
          <h3>Professions</h3>
          <div className="profession-grid">
            <div className="profession-item">
              <span className="profession-icon">🔬</span>
              <span className="profession-label">Scientists</span>
              <span className="profession-value">{reportData.population?.demographics?.scientists}</span>
            </div>
            <div className="profession-item">
              <span className="profession-icon">⚙️</span>
              <span className="profession-label">Engineers</span>
              <span className="profession-value">{reportData.population?.demographics?.engineers}</span>
            </div>
            <div className="profession-item">
              <span className="profession-icon">🌱</span>
              <span className="profession-label">Farmers</span>
              <span className="profession-value">{reportData.population?.demographics?.farmers}</span>
            </div>
            <div className="profession-item">
              <span className="profession-icon">🏥</span>
              <span className="profession-label">Medics</span>
              <span className="profession-value">{reportData.population?.demographics?.medics}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="vital-stats">
        <div className="vital-item">
          <h4>Births This Month</h4>
          <div className="vital-value positive">+{reportData.population?.births}</div>
        </div>
        <div className="vital-item">
          <h4>Deaths This Month</h4>
          <div className="vital-value negative">-{reportData.population?.deaths}</div>
        </div>
        <div className="vital-item">
          <h4>Net Change</h4>
          <div
            className={`vital-value ${(reportData.population?.births - reportData.population?.deaths) >= 0 ? "positive" : "negative"}`}
          >
            {reportData.population?.births - reportData.population?.deaths >= 0 ? "+" : ""}
            {reportData.population?.births - reportData.population?.deaths}
          </div>
        </div>
      </div>
    </div>
  )

  const renderResourceReport = () => (
    <div className="report-content">
      <div className="report-header">
        <h2>Resource Management</h2>
        <div className="efficiency-score">
          <span className="efficiency-value" style={{ color: getStatusColor(reportData.resources?.efficiency) }}>
            {reportData.resources?.efficiency?.toFixed(1)}%
          </span>
          <span className="efficiency-label">Overall Efficiency</span>
        </div>
      </div>

      <div className="resource-grid">
        {Object.entries(reportData.resources?.current || {}).map(([resource, amount]) => (
          <div key={resource} className="resource-card">
            <div className="resource-header">
              <span className="resource-icon">
                {resource === "energy"
                  ? "⚡"
                  : resource === "food"
                    ? "🍎"
                    : resource === "water"
                      ? "💧"
                      : resource === "materials"
                        ? "🔧"
                        : resource === "oxygen"
                          ? "🫁"
                          : resource === "knowledge"
                            ? "🔬"
                            : resource === "population"
                              ? "👥"
                              : "📦"}
              </span>
              <h3>{resource.charAt(0).toUpperCase() + resource.slice(1)}</h3>
            </div>

            <div className="resource-amount">{amount?.toLocaleString()}</div>

            <div className="resource-details">
              <div className="resource-flow">
                <span className="flow-item production">+{reportData.resources?.production?.[resource] || 0}/day</span>
                <span className="flow-item consumption">-{reportData.resources?.consumption?.[resource] || 0}/day</span>
              </div>

              {reportData.resources?.reserves?.[resource] && (
                <div className="reserve-days">{reportData.resources.reserves[resource]} days reserve</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderInfrastructureReport = () => (
    <div className="report-content">
      <div className="report-header">
        <h2>Infrastructure Status</h2>
        <div className="building-summary">
          <span className="building-count">{reportData.infrastructure?.totalBuildings}</span>
          <span className="building-label">Total Buildings</span>
        </div>
      </div>

      <div className="infrastructure-overview">
        <div className="building-health">
          <h3>Building Health Overview</h3>
          <div className="health-bar-large">
            <div
              className="health-fill"
              style={{
                width: `${reportData.infrastructure?.averageHealth}%`,
                backgroundColor: getStatusColor(reportData.infrastructure?.averageHealth),
              }}
            />
          </div>
          <div className="health-value">{reportData.infrastructure?.averageHealth?.toFixed(1)}% Average Health</div>
        </div>

        <div className="buildings-list">
          <h3>Current Buildings</h3>
          <div className="buildings-grid">
            {gameState.buildings.map((building, index) => (
              <div key={index} className="building-item">
                <div className="building-info">
                  <span className="building-icon">
                    {building.name.includes("Solar")
                      ? "☀️"
                      : building.name.includes("Farm")
                        ? "🌱"
                        : building.name.includes("Lab")
                          ? "🔬"
                          : building.name.includes("Medical")
                            ? "🏥"
                            : building.name.includes("Mining")
                              ? "⛏️"
                              : "🏗️"}
                  </span>
                  <div className="building-details">
                    <h4>{building.name}</h4>
                    <div className="building-level">Level {building.level}</div>
                  </div>
                </div>
                <div className="building-health-indicator">
                  <div
                    className="health-circle"
                    style={{
                      background: `conic-gradient(${getStatusColor(building.health)} ${building.health * 3.6}deg, rgba(255,255,255,0.1) 0deg)`,
                    }}
                  >
                    <span className="health-text">{building.health}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {reportData.infrastructure?.constructionQueue?.length > 0 && (
          <div className="construction-queue">
            <h3>Construction Queue</h3>
            <div className="queue-list">
              {reportData.infrastructure.constructionQueue.map((project, index) => (
                <div key={index} className="queue-item">
                  <div className="project-info">
                    <h4>{project.name}</h4>
                    <div className="project-eta">ETA: {project.eta}</div>
                  </div>
                  <div className="project-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${project.progress}%` }} />
                    </div>
                    <span className="progress-text">{project.progress}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {reportData.infrastructure?.maintenanceNeeded?.length > 0 && (
          <div className="maintenance-alerts">
            <h3>⚠️ Maintenance Required</h3>
            <div className="alert-list">
              {reportData.infrastructure.maintenanceNeeded.map((building, index) => (
                <div key={index} className="alert-item">
                  <span className="alert-icon">🔧</span>
                  <span className="alert-text">
                    {building.name} - {building.health}% health
                  </span>
                  <span className="alert-priority">High Priority</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const renderThreatReport = () => (
    <div className="report-content">
      <div className="report-header">
        <h2>Threat Assessment</h2>
        <div className="threat-level">
          <span className="threat-value" style={{ color: getStatusColor(reportData.threats?.level, "threat") }}>
            {reportData.threats?.level}
          </span>
          <span className="threat-label">Current Threat Level</span>
        </div>
      </div>

      <div className="threats-overview">
        <div className="active-threats">
          <h3>🚨 Active Threats</h3>
          <div className="threats-list">
            {reportData.threats?.active?.map((threat, index) => (
              <div key={index} className="threat-item">
                <div className="threat-info">
                  <h4>{threat.name}</h4>
                  <div className="threat-details">
                    {threat.eta && <span className="threat-eta">ETA: {threat.eta}</span>}
                    {threat.location && <span className="threat-location">Location: {threat.location}</span>}
                    {threat.type && <span className="threat-type">Type: {threat.type}</span>}
                  </div>
                </div>
                <div className="threat-severity" style={{ color: getStatusColor(threat.severity, "threat") }}>
                  {threat.severity}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="environmental-threats">
          <h3>🌪️ Environmental Risks</h3>
          <div className="env-threats-list">
            {reportData.threats?.environmental?.map((threat, index) => (
              <div key={index} className="env-threat-item">
                <div className="env-threat-info">
                  <h4>{threat.name}</h4>
                  <div className="env-threat-impact">{threat.impact}</div>
                </div>
                <div className="env-threat-probability">
                  <div className="probability-bar">
                    <div className="probability-fill" style={{ width: `${threat.probability}%` }} />
                  </div>
                  <span className="probability-text">{threat.probability}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="recommendations">
          <h3>💡 Recommendations</h3>
          <div className="recommendations-list">
            {reportData.threats?.recommendations?.map((rec, index) => (
              <div key={index} className="recommendation-item">
                <span className="rec-icon">📋</span>
                <span className="rec-text">{rec}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const renderAchievementsReport = () => (
    <div className="report-content">
      <div className="report-header">
        <h2>Achievements & Progress</h2>
        <div className="achievement-score">
          <span className="score-value">{reportData.achievements?.recent?.length || 0}</span>
          <span className="score-label">Recent Achievements</span>
        </div>
      </div>

      <div className="achievements-overview">
        <div className="recent-achievements">
          <h3>🏆 Recent Achievements</h3>
          <div className="achievements-list">
            {reportData.achievements?.recent?.map((achievement, index) => (
              <div key={index} className="achievement-item">
                <div className="achievement-icon">🏆</div>
                <div className="achievement-info">
                  <h4>{achievement.name}</h4>
                  <div className="achievement-desc">{achievement.description}</div>
                  <div className="achievement-date">{achievement.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="milestones">
          <h3>🎯 Colony Milestones</h3>
          <div className="milestones-list">
            {reportData.achievements?.milestones?.map((milestone, index) => (
              <div key={index} className={`milestone-item ${milestone.completed ? "completed" : "pending"}`}>
                <div className="milestone-status">{milestone.completed ? "✅" : "⏳"}</div>
                <div className="milestone-info">
                  <h4>{milestone.name}</h4>
                  {milestone.completed ? (
                    <div className="milestone-date">Completed: {milestone.date}</div>
                  ) : (
                    <div className="milestone-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${milestone.progress}%` }} />
                      </div>
                      <span className="progress-text">{milestone.progress}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="progress-metrics">
          <h3>📊 Progress Metrics</h3>
          <div className="metrics-grid">
            {Object.entries(reportData.achievements?.progress || {}).map(([metric, value]) => (
              <div key={metric} className="metric-item">
                <h4>{metric.charAt(0).toUpperCase() + metric.slice(1)}</h4>
                <div className="metric-bar">
                  <div
                    className="metric-fill"
                    style={{
                      width: `${value}%`,
                      backgroundColor: getStatusColor(value),
                    }}
                  />
                </div>
                <div className="metric-value">{value}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const reportTabs = [
    { id: "overview", name: "Overview", icon: "📊" },
    { id: "population", name: "Population", icon: "👥" },
    { id: "resources", name: "Resources", icon: "⚡" },
    { id: "infrastructure", name: "Infrastructure", icon: "🏗️" },
    { id: "threats", name: "Threats", icon: "⚠️" },
    { id: "achievements", name: "Achievements", icon: "🏆" },
  ]

  const renderReport = () => {
    switch (activeReport) {
      case "overview":
        return renderOverviewReport()
      case "population":
        return renderPopulationReport()
      case "resources":
        return renderResourceReport()
      case "infrastructure":
        return renderInfrastructureReport()
      case "threats":
        return renderThreatReport()
      case "achievements":
        return renderAchievementsReport()
      default:
        return renderOverviewReport()
    }
  }

  return (
    <div className="colony-reports">
      <div className="reports-header">
        <h1 className="reports-title">
          <span className="title-icon">📊</span>
          Colony Status Reports
        </h1>
        <p className="reports-subtitle">Comprehensive analysis of your space colony</p>
      </div>

      <div className="reports-navigation">
        {reportTabs.map((tab) => (
          <button
            key={tab.id}
            className={`report-tab ${activeReport === tab.id ? "active" : ""}`}
            onClick={() => setActiveReport(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-name">{tab.name}</span>
          </button>
        ))}
      </div>

      <div className="reports-container">{renderReport()}</div>
    </div>
  )
}

export default ColonyReports
